import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useSaleLock from '../hooks/useSaleLock';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { setCalendarData } from '../store/calendarSlice';
import { addTransaction } from '../store/salesSlice';
import { setBonusPrizeData } from '../store/bonusPrizeSlice';
import { PW_Products_API, PW_Stores_API, PW_Calendar_API, PW_BonusPrize_API, PW_Coupon_Generate_API, PW_Coupon_Sell_API, PW_Transaction_Counts_API } from '../Server/ApiV2';
import Spinner from './Spinner';
import Pick6Round from './Pick6Round';
import { isPick6, pick6Refusal } from '../utils/pick6';

// 20 unique random labels (1–99) shown on the face-down cards.
// Purely cosmetic — the pick still works by position, so selectionNo is unchanged.
function pickCardLabels() {
  const pool = Array.from({ length: 99 }, (_, i) => i + 1);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, TILE_COUNT);
}

// Face-down tiles shown to the customer. The API returns 10 PREVIEW cards; the
// card actually sold is decided and returned by /coupon/sell on pick, so the
// tile count is cosmetic and we pad the preview array to fill the grid.
const TILE_COUNT = 20;
const padTiles = (cards) =>
  Array.from({ length: TILE_COUNT }, (_, i) => cards[i % cards.length]);

// A failed call is not "no schedule". httpRequest hands back the axios error when
// a call fails, and treating that like schedule:false showed "Schedule Not Set"
// for every outage. With no reply at all a sale may still have been committed on
// the server, so the operator is told to check before selling again.
const requestFailure = (res, isSale) => {
  if (res?.response) {
    const why = res.response.data?.message || `HTTP ${res.response.status}`;
    return { title: 'Server Error', text: `${isSale ? 'The sale was not saved. ' : ''}${why}. Please try again.` };
  }
  return isSale
    ? { title: 'Sale Not Confirmed', text: 'No reply from the server. The sale may still have been saved — check the Transactions report before selling again.' }
    : { title: 'Server Not Reachable', text: 'Could not reach the server. Check the connection and try again.' };
};

export default function Coupon() {
  const dispatch = useDispatch();
  const { products, productTypes, productTypeLinks } = useSelector(s => s.product);
  const { stores } = useSelector(s => s.admin);
  const { schedules } = useSelector(s => s.calendar);
  const { bonusSchedules } = useSelector(s => s.bonusPrize);
  const { user } = useSelector(s => s.user);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // No transaction list, and only schedules that haven't ended — the page
      // only ever matches today's schedule, and the rotation display uses the
      // small per-store counts below instead of every sale ever recorded.
      const [prodRes, storeRes, calRes, bonusRes] = await Promise.all([
        PW_Products_API(), PW_Stores_API(), PW_Calendar_API({ current: 1 }), PW_BonusPrize_API(),
      ]);
      if (cancelled) return;
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      if (calRes?.data?.success) dispatch(setCalendarData(calRes.data.data));
      if (bonusRes?.data?.success) dispatch(setBonusPrizeData(bonusRes.data.data));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const activeStores = stores.filter(s => s.status === 'Active');
  const [storeId, setStoreId] = useState(activeStores[0]?.id || '');
  const [productId, setProductId] = useState(products[0]?.id || '');
  const [cards, setCards] = useState(null);
  const [cardLabels, setCardLabels] = useState([]);
  const [pickedIdx, setPickedIdx] = useState(null);
  const [picking, setPicking] = useState(false);
  const [revealed, setRevealed] = useState(false);
  // 'tap' = customer picks one of the 10 tiles (original flow, always the
  // default). 'number' = customer calls out any 2-digit number and the operator
  // keys it in. Engagement only — the number is the recorded selection number,
  // it does NOT feed the sale: /coupon/sell decides win/lose from the store's
  // seeded schedule and never sees a tile position.
  const [entryMode, setEntryMode] = useState('tap');
  const [luckyNo, setLuckyNo] = useState('');
  const [chosenNo, setChosenNo] = useState(null);
  // A Pick 6 round replaces the 20 tiles: { key, optionCount } from generate.
  const [pick6Round, setPick6Round] = useState(null);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const getSchedule = (sid, pid) => {
    const a = schedules.filter(s => s.status === 'Active' && s.storeIds.includes(sid)
      && (!s.productIds?.length || s.productIds.includes(pid)));
    return a.find(s => {
      const start = (s.startDate || '').slice(0, 10);
      const end = (s.endDate || '').slice(0, 10);
      return todayStr >= start && todayStr <= end;
    }) || null;
  };

  const schedule = getSchedule(storeId, productId);
  const allTypeOrder = schedule?.productTypeOrder || [];
  const linkedTypeIds = productTypeLinks.filter(l => l.linked && l.productId === productId).map(l => l.typeId);
  const typeOrder = allTypeOrder.filter(tid => linkedTypeIds.includes(tid));
  const product = products.find(p => p.id === productId);
  const pick6 = isPick6(product);
  const batchSize = product?.cardsPerBundle || 100;
  // resetDaily=true (Classic): counter + winner schedule reset each day.
  // resetDaily=false (Super/Supreme): bundle balance + winner schedule carry over to the next day.
  const resetDaily = product?.resetDaily !== false;

  // Sale counts per store+product (today + all-time) from the server — all the
  // rotation display needs. Merged per store so a slow reply for a store the
  // operator has already switched away from can't overwrite the current one.
  const [counts, setCounts] = useState([]);
  const loadCounts = useCallback(async (sid) => {
    if (!sid) return;
    const res = await PW_Transaction_Counts_API({ storeId: sid });
    if (res?.data?.success) {
      const rows = res.data.data || [];
      setCounts(prev => [...prev.filter(c => c.storeId !== sid), ...rows]);
    }
  }, []);
  useEffect(() => { loadCounts(storeId); }, [storeId, loadCounts]);
  const countRow = counts.find(c => c.storeId === storeId && c.productId === productId);
  const scopedTotal = countRow ? (resetDaily ? countRow.todayCount : countRow.totalCount) : 0;

  const { activeTypeId, activeIdx, currentCycle } = useMemo(() => {
    if (!typeOrder.length || !product) return { activeTypeId: null, activeIdx: -1, currentCycle: 1 };
    const total = scopedTotal;
    const cycleSize = typeOrder.length * batchSize;
    const posInCycle = total % cycleSize;
    const idx = Math.floor(posInCycle / batchSize);
    return {
      // idx is the position in the rotation — with a repeated type only THIS slot is
      // active, not every occurrence of the same type id.
      activeTypeId: typeOrder[idx] || typeOrder[0],
      activeIdx: typeOrder[idx] !== undefined ? idx : 0,
      currentCycle: Math.floor(total / cycleSize) + 1,
    };
  }, [typeOrder, scopedTotal, product, batchSize]);

  const link = activeTypeId
    ? productTypeLinks.find(l => l.productId === productId && l.typeId === activeTypeId && l.linked)
    : null;

  const [generating, setGenerating] = useState(false);
  const [genProduct, setGenProduct] = useState(null);

  // One till at a time per (store, product). This holds the same slot the mobile
  // POS tills hold, so a web round and a till round for the same store + product
  // serialise — the second operator waits until the first finishes the sale or
  // leaves this screen (strict block, bounded by the server's TTL backstop).
  const { acquire, release, watch, cancel, waiting: waitingForSlot, busyMap } = useSaleLock(user?.userName);
  // The (store, product) we currently hold the slot for, or null. release() must
  // name the same pair it acquired, and storeId/productId state can change while
  // we still owe a release, so the held pair is tracked explicitly.
  const heldRef = useRef(null);

  const dropLock = useCallback(() => {
    cancel();
    const h = heldRef.current;
    if (!h) return;
    heldRef.current = null;
    release(h.storeId, h.productId);
  }, [cancel, release]);

  // Observe the selected store's busy slots (drives the "another user is
  // serving" hint before Generate is pressed).
  useEffect(() => { if (storeId) watch(storeId); }, [storeId, watch]);

  // Release on unmount (navigating away from the page mid-round).
  useEffect(() => dropLock, [dropLock]);

  const busyHolder = busyMap.get(productId) || null;

  // When no schedule is configured for the selected store + product on the
  // transaction date (today), alert the operator to contact the admin.
  const [noSchedDialog, setNoSchedDialog] = useState(false);
  const [requestError, setRequestError] = useState(null);
  useEffect(() => {
    setNoSchedDialog(!pageLoading && !!storeId && !!productId && !schedule);
  }, [pageLoading, storeId, productId, schedule]);

  const handleGenerate = async () => {
    if (!product || !link || generating || waitingForSlot) return;
    // Claim the counter BEFORE generating. A second operator waits here on
    // "another user is serving" rather than racing into the same batch position.
    // The wait ends when the holder finishes their sale or leaves the screen.
    const slot = await acquire(storeId, productId);
    if (!slot.granted) return; // round abandoned while queued
    heldRef.current = { storeId, productId };
    setGenerating(true);
    try {
      // Generation logic lives in the API (single source of truth for Web + Mobile):
      // type rotation w/ ResetDaily, seeded winner schedule, bonus-first priority.
      const res = await PW_Coupon_Generate_API({ storeId, productId });
      const r = res?.data?.success ? res.data.data : null;
      if (!r) { dropLock(); setRequestError(requestFailure(res, false)); return; }
      if (!r.schedule) { dropLock(); showRefusal(r); return; }
      setGenProduct(product);
      if (r.pick6) {
        // The lock stays held until Confirm (the sale) or the 20-second timer
        // runs out.
        setPick6Round({ key: Date.now(), optionCount: r.optionCount || 0 });
        return;
      }
      // Preview tiles only — the winning card is decided + returned by /sell on
      // pick, so we don't trust or use any outcome from generate here.
      setCards(padTiles(r.cards));
      setCardLabels(pickCardLabels());
      setPickedIdx(null);
      setRevealed(false);
    } catch (err) {
      console.error('[Coupon] Generate failed:', err);
      dropLock();
    } finally {
      setGenerating(false);
    }
  };

  // Picking a card commits the sale on the server. The API decides win/lose,
  // builds the sold card and records the ticket atomically (per-store lock), then
  // returns the authoritative card which we drop into the picked slot to reveal.
  // `overrideNo` is the number the customer called out in 'number' entry mode.
  // It replaces the tile's face value as the recorded selection number; the
  // server sale, prize and sequence are untouched.
  const handlePick = async (idx, overrideNo) => {
    if (pickedIdx !== null || picking) return;
    setPicking(true);
    setPickedIdx(idx);
    // The card's identity is the number shown to the customer — the tile's face
    // value, or the one they called out — not the 1–10 tile position.
    const selNo = overrideNo != null ? overrideNo : cardLabels[idx] ?? idx + 1;
    setChosenNo(selNo);
    try {
      const res = await PW_Coupon_Sell_API({ storeId, productId, selectionNo: selNo, userId: user?.userId });
      const r = res?.data?.success ? res.data.data : null;
      if (!r || !r.schedule) {
        setPickedIdx(null);
        setChosenNo(null);
        if (r) setNoSchedDialog(true);
        else setRequestError(requestFailure(res, true));
        return;
      }
      const card = r.card;
      const next = [...cards];
      next[idx] = card;
      setCards(next);

      const store = stores.find(s => s.id === storeId);
      dispatch(addTransaction({
        id: r.id,
        selectionNo: selNo,
        storeSeq: r.storeSeq,
        storeId,
        storeName: store?.name || '',
        productId,
        productName: r.productName || (genProduct || product)?.name || '',
        typeId: r.activeTypeId,
        cardGrid: card.grid,
        isWinner: card.isWinner,
        winDenomination: card.winningNumber,
        winningNumbers: card.winningNumbers || (card.isWinner ? [card.winningNumber] : []),
        winPairs: card.winPairs || null,
        prize: r.prize,
        priceAmount: r.factor,
        bonusAmount: 0,
        bonusScheduleId: r.bonusScheduleId || null,
        userId: user?.userId || '',
        userName: user?.userName || '',
        timestamp: new Date().toISOString(),
      }));
      // Re-read the counters so the display also reflects other tills' sales.
      loadCounts(storeId);

      // Sale saved — hand the counter to the next operator right away. The round
      // stays on screen for the reveal, but the slot is not held across it (a
      // reveal the operator forgets to close must never freeze the counter).
      dropLock();
    } catch (err) {
      console.error('[Coupon] Sell failed:', err);
      setPickedIdx(null);
      setChosenNo(null);
    } finally {
      setPicking(false);
    }
  };

  // schedule:false with a Pick 6 reason gets its own message; without one it is
  // the usual "no schedule" dialog.
  const showRefusal = (r) => {
    const refusal = pick6Refusal(r.reason);
    if (refusal) setRequestError(refusal);
    else setNoSchedDialog(true);
  };

  // Confirm with the customer's 6 numbers. Returns the sale for the round to
  // show, or null after closing the round and saying why.
  const handlePick6Sell = async (picks) => {
    const endRound = () => { dropLock(); setPick6Round(null); setGenProduct(null); };
    try {
      const res = await PW_Coupon_Sell_API({ storeId, productId, selectionNo: null, userId: user?.userId, picks });
      const r = res?.data?.success ? res.data.data : null;
      if (!r || !r.schedule || !r.pick6) {
        endRound();
        if (!r) setRequestError(requestFailure(res, true));
        else showRefusal(r);
        return null;
      }
      loadCounts(storeId);
      dropLock();
      return { ...r, soldAt: new Date().toISOString() };
    } catch (err) {
      console.error('[Coupon] Pick 6 sell failed:', err);
      endRound();
      setRequestError(requestFailure(null, true));
      return null;
    }
  };

  const handlePick6Timeout = useCallback(() => {
    dropLock();
    setPick6Round(null);
    setGenProduct(null);
    setRequestError({ title: "Time's Up", text: 'The numbers weren\'t confirmed within 20 seconds, so nothing was sold. Press Start to begin again.' });
  }, [dropLock]);

  const getActiveBonuses = () => {
    if (!storeId || !productId) return [];
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return bonusSchedules.filter(bs => {
      const bsDate = bs.scheduleDate ? bs.scheduleDate.split('T')[0] : '';
      return bsDate === todayStr && bs.productId === productId && bs.status === 'Active'
        && (bs.storeIds || []).includes(storeId)
        && hhmm >= bs.startTime && hhmm <= bs.endTime;
    });
  };

  const handleReveal = () => {
    if (pickedIdx === null || revealed) return;
    setRevealed(true);
  };

  const handleNewRound = () => {
    // Frees the slot if a round was generated but abandoned before the sale
    // (e.g. the operator switches store/product). Idempotent after a sale, which
    // already released. Also cancels a queued acquire that never got the slot.
    dropLock();
    setCards(null);
    setPick6Round(null);
    setPickedIdx(null);
    setRevealed(false);
    setGenProduct(null);
    setEntryMode('tap');
    setLuckyNo('');
    setChosenNo(null);
  };

  const pickedCard = pickedIdx !== null ? cards[pickedIdx] : null;
  const posInType = typeOrder.length ? scopedTotal % (typeOrder.length * batchSize) % batchSize : 0;
  const remaining = batchSize - posInType;

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Coupon Sales</h2>
          <div className="page-header-sub">Generate instance rewards for customers</div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-title gold">Select Store & Product</div>
        <div className="card-body">
          <div className="coupon-pickers">
            <div>
              <div className="text-sm text-muted mb-8">Store</div>
              <select className="form-select" value={storeId}
                onChange={e => { setStoreId(e.target.value); handleNewRound(); }}>
                {activeStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-sm text-muted mb-8">Product</div>
              <select className="form-select" value={productId}
                onChange={e => { setProductId(e.target.value); handleNewRound(); }}>
                {products.filter(p => p.status === 'Active').map(p => (
                  <option key={p.id} value={p.id}>{p.name} (RSP {p.rsp} · Price {p.priceAmount || 1})</option>
                ))}
              </select>
            </div>
          </div>

          {schedule ? (
            <div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <div>
                  <span className="text-sm text-muted">Schedule: </span>
                  {typeOrder.map((tid, i) => (
                    <React.Fragment key={`${tid}-${i}`}>
                      {i > 0 && <span className="cal-arrow">→</span>}
                      <span className={`chip ${i === activeIdx ? 'chip-green' : 'chip-purple'}`} style={{ fontSize: 11 }}>{tid}</span>
                    </React.Fragment>
                  ))}
                  <span className="text-sm text-muted" style={{ marginLeft: 12 }}>Cycle {currentCycle}</span>
                  {(() => {
                    const abs = getActiveBonuses();
                    return abs.length > 0 ? (
                      <span className="chip chip-gold" style={{ marginLeft: 8, fontSize: 10 }}>
                        +{abs.length} Bonus {abs.length > 1 ? 'Schedules' : 'Schedule'}
                      </span>
                    ) : null;
                  })()}
                </div>
                {link ? (
                  <button className="btn btn-success" onClick={handleGenerate}
                    disabled={!!cards || !!pick6Round || generating || waitingForSlot}>
                    {waitingForSlot ? 'Waiting for another user…' : generating ? 'Loading...' : pick6 ? `Start ${product.name}` : 'Generate 10 Coupons'}
                  </button>
                ) : (
                  <span className="text-sm" style={{ color: '#e74c3c' }}>
                    Type {activeTypeId} not linked to {product?.name}
                  </span>
                )}
              </div>
              {busyHolder && !cards && !pick6Round && (
                <div className="text-sm" style={{ color: '#c9821f', fontWeight: 600, marginBottom: 6 }}>
                  ● Another user ({busyHolder}) is serving this product now — Generate will wait until they finish.
                </div>
              )}
              <div className="text-sm text-muted">
                Active: <strong style={{ color: '#00895e' }}>{activeTypeId}</strong>
                {' '} — {remaining} of {batchSize} remaining
                {typeOrder.length > 1 && (
                  <span> — then {[...typeOrder.slice(activeIdx + 1), ...typeOrder.slice(0, activeIdx)].join(' → ')}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm" style={{ color: '#e74c3c', padding: '8px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>Schedule not set for this store &amp; product today. Contact your Administrator.</span>
              <button className="btn btn-outline btn-sm" onClick={() => setNoSchedDialog(true)}>Details</button>
            </div>
          )}
        </div>
      </div>

      {cards && (
        <div className="card mb-24">
          <div className={`card-title ${revealed && pickedCard?.isWinner ? (pickedCard.isBonusWinner ? 'gold' : 'green') : 'purple'}`}>
            {pickedIdx === null ? 'Pick Your Lucky Card!' : revealed ? (pickedCard?.isWinner ? (pickedCard.isBonusWinner ? 'Bonus Winner!' : 'Winner!') : 'Better luck next time') : 'Scratch to Reveal'}
          </div>
          <div className="card-body">
            {pickedIdx === null && (
              <div className="entry-mode-row">
                <button type="button"
                  className={`entry-mode-btn${entryMode === 'tap' ? ' active' : ''}`}
                  onClick={() => setEntryMode('tap')}>Tap a Card</button>
                <button type="button"
                  className={`entry-mode-btn${entryMode === 'number' ? ' active' : ''}`}
                  onClick={() => setEntryMode('number')}>Make your Number</button>
              </div>
            )}

            {entryMode === 'number' && pickedIdx === null ? (
              <div className="lucky-entry">
                <div className="lucky-prompt">Ask the customer for a lucky number</div>
                {/* No maxLength — it counts raw keystrokes, so a stray letter
                    fills the quota and silently eats the next digit. The strip
                    below bounds the value instead. */}
                <input className="lucky-input" type="text" inputMode="numeric"
                  placeholder="--" value={luckyNo} autoFocus
                  onChange={e => setLuckyNo(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  onKeyDown={e => { if (e.key === 'Enter' && luckyNo.length === 2) handlePick(0, Number(luckyNo)); }} />
                <button className="btn btn-success" disabled={luckyNo.length !== 2 || picking}
                  onClick={() => handlePick(0, Number(luckyNo))}>Confirm</button>
              </div>
            ) : (
              <div className="scratch-grid">
                {cards.map((card, idx) => {
                  const isPicked = pickedIdx === idx;
                  const isOther = pickedIdx !== null && !isPicked;
                  return (
                    <div key={idx}
                      className={`scratch-card${isPicked ? ' picked' : ''}${isOther ? ' dimmed' : ''}${isPicked && revealed && card.isWinner ? ' winner-glow' : ''}`}
                      onClick={() => handlePick(idx)}>
                      <div className="scratch-card-num">{cardLabels[idx] ?? idx + 1}</div>
                      <div className="scratch-card-star">★</div>
                    </div>
                  );
                })}
              </div>
            )}

            {pickedIdx !== null && (
              <div className="scratch-reveal">
                <div>
                  {chosenNo != null && (
                    <div className="chosen-no">You have chosen <strong>#{chosenNo}</strong></div>
                  )}
                  <div className="scratch-grid-4x4">
                    {pickedCard.grid.flat().map((val, i) => (
                      <div key={i} className={`scratch-cell${revealed ? ' open' : ''}${revealed && pickedCard.isWinner && (pickedCard.winningNumbers ? pickedCard.winningNumbers.includes(val) : val === pickedCard.winningNumber) ? ' win-cell' : ''}`}>
                        {revealed ? val.toLocaleString() : '?'}
                      </div>
                    ))}
                  </div>
                  {!revealed && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <button className="btn btn-success" onClick={handleReveal}>Open Card</button>
                    </div>
                  )}
                  {revealed && (
                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                      {pickedCard.isWinner ? (() => {
                        const f = (genProduct || product)?.priceAmount || 1;
                        const base = pickedCard.winAmount * f;
                        return (
                          <div className="scratch-prize">
                            Prize: {pickedCard.winPairs && pickedCard.winPairs.length > 1
                              ? `(${pickedCard.winPairs.map(p => p.toLocaleString()).join(' + ')})${f > 1 ? ` × ${f}` : ''} = ${base.toLocaleString()}`
                              : `${pickedCard.winAmount.toLocaleString()}${f > 1 ? ` × ${f} = ${base.toLocaleString()}` : ''}`}
                          </div>
                        );
                      })() : (
                        <div className="text-muted fw-600">No prize this time</div>
                      )}
                      <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={handleNewRound}>New Round</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {pick6Round && (
        <Pick6Round key={pick6Round.key}
          product={genProduct || product}
          optionCount={pick6Round.optionCount}
          store={stores.find(s => s.id === storeId)}
          userName={user?.userName}
          onSell={handlePick6Sell}
          onTimeout={handlePick6Timeout}
          onNewRound={handleNewRound} />
      )}

      {noSchedDialog && (
        <div className="modal-overlay" onClick={() => setNoSchedDialog(false)}>
          <div className="modal-content" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              Schedule Not Set
              <button className="modal-close" onClick={() => setNoSchedDialog(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 46, marginBottom: 10 }}>🗓️</div>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#0f4c81', marginBottom: 12 }}>
                No schedule is configured for this selection.
              </p>
              <div style={{ fontSize: 13, color: '#636e72', lineHeight: 1.8, marginBottom: 14 }}>
                <div>Store: <strong>{stores.find(s => s.id === storeId)?.name || '—'}</strong></div>
                <div>Product: <strong>{product?.name || '—'}</strong></div>
                <div>Transaction Date: <strong>{todayStr}</strong></div>
              </div>
              <p style={{ fontSize: 14, color: '#e74c3c', fontWeight: 600, margin: 0 }}>
                Please contact your Administrator to set up the schedule.
              </p>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={() => setNoSchedDialog(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {requestError && (
        <div className="modal-overlay" onClick={() => setRequestError(null)}>
          <div className="modal-content" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              {requestError.title}
              <button className="modal-close" onClick={() => setRequestError(null)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: '#e74c3c', fontWeight: 600, lineHeight: 1.6, margin: 0 }}>
                {requestError.text}
              </p>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={() => setRequestError(null)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
